import CustomBreadcrumb from '@/components/custom-breadcrumb/custom-breadcrumb';
import { useBreadcrumbs } from '@/hooks/use-breadcrumbs/use-breadcrumbs';
import { Alert, Flex, Input, Spin, Tag, message } from 'antd';
import styles from './voices.module.scss';
import { useUser } from '@/hooks/use-user/use-user';
import { useCompaniesList } from '@/hooks/use-company/use-company';
import { useVoices } from '@/hooks/use-voices/use-voices';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/button/button';
import { PlayCircleOutlined, PauseCircleOutlined, CopyOutlined } from '@ant-design/icons';
import Search from '@/assets/icons/search-md.svg?react';
import Dropdown from '@/components/form/dropdown/dropdown';
import { Voice } from '@/services/voices-service/voices.service.types';
import { useTranslation } from 'react-i18next';
import {
  CATEGORY_FILTERS,
  categoryLabel,
} from '@/components/voice-selector-drawer/voice-labels';

export function Voices() {
  const { t } = useTranslation(['voices', 'settings', 'common']);
  const items = useBreadcrumbs();
  const { userData } = useUser();
  const { companies } = useCompaniesList();
  const isAdmin = userData?.is_admin || false;

  const [selectedCompanyId, setSelectedCompanyId] = useState<number | undefined>();
  const [search, setSearch] = useState<string | undefined>();
  const [category, setCategory] = useState<string | undefined>();
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (userData?.company?.id && !selectedCompanyId) {
      setSelectedCompanyId(userData.company.id);
    }
  }, [userData?.company?.id, selectedCompanyId]);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
    };
  }, []);

  const { voices, isPending, isError } = useVoices(selectedCompanyId, search, category);

  const companyOptions =
    companies?.map((company) => ({
      label: company.name,
      value: company.id,
    })) || [];

  const handlePlayPreview = (voice: Voice) => {
    if (!voice.preview_url) return;

    if (playingVoiceId === voice.voice_id) {
      audioRef.current?.pause();
      setPlayingVoiceId(null);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
    }

    const audio = new Audio(voice.preview_url);
    audio.onended = () => setPlayingVoiceId(null);
    audio.play();
    audioRef.current = audio;
    setPlayingVoiceId(voice.voice_id);
  };

  const handleCopyId = (voiceId: string) => {
    navigator.clipboard.writeText(voiceId);
    message.success(t('voices:idCopied'));
  };

  return (
    <Flex vertical className={styles.container}>
      <CustomBreadcrumb items={items} />
      <div className={styles.header}>
        <div className={styles.filters}>
          {/* One company is nothing to switch to. The effect above selects it anyway. */}
          {isAdmin && companyOptions.length !== 1 && (
            <Dropdown
              className={styles.dropdown}
              wrapperClassName={styles.wrapperClassName}
              placeholder={t('settings:companySelect.placeholder')}
              value={selectedCompanyId}
              onChange={setSelectedCompanyId}
              options={companyOptions}
            />
          )}
          <Flex vertical gap={5} className={styles.searchContainer}>
            <Input
              onChange={(e) => setSearch(e.target.value || undefined)}
              value={search || ''}
              placeholder={t('common:actions.search')}
              prefix={<Search stroke={styles.iconColor} height={16} width={16} strokeWidth={2.5} />}
            />
          </Flex>
          <Dropdown
            className={styles.dropdown}
            wrapperClassName={styles.wrapperClassName}
            placeholder={t('voices:filters.categoryPlaceholder')}
            value={category}
            onChange={setCategory}
            options={CATEGORY_FILTERS.map((value) => ({
              label: t(`voices:categories.${value}`),
              value,
            }))}
          />
        </div>
      </div>

      {isError && (
        <Alert
          type="warning"
          message={t('voices:libraryUnavailable')}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {isPending ? (
        <Flex justify="center" style={{ padding: 48 }}>
          <Spin size="large" />
        </Flex>
      ) : voices && voices.length > 0 ? (
        <div className={styles.grid}>
          {voices.map((voice) => (
            <div key={voice.voice_id} className={styles.voiceCard}>
              <div className={styles.voiceCardHeader}>
                <div>
                  <p className={styles.voiceName}>{voice.name}</p>
                  <span className={styles.voiceCategory}>{categoryLabel(voice.category)}</span>
                </div>
              </div>
              {voice.description && <p className={styles.voiceDescription}>{voice.description}</p>}
              {voice.labels && Object.keys(voice.labels).length > 0 && (
                <div className={styles.labels}>
                  {Object.entries(voice.labels).map(([key, value]) => (
                    <Tag key={key}>{value}</Tag>
                  ))}
                </div>
              )}
              <div className={styles.voiceActions}>
                {voice.preview_url && (
                  <Button
                    type="text"
                    size="small"
                    icon={
                      playingVoiceId === voice.voice_id ? (
                        <PauseCircleOutlined />
                      ) : (
                        <PlayCircleOutlined />
                      )
                    }
                    onClick={() => handlePlayPreview(voice)}
                  >
                    {playingVoiceId === voice.voice_id
                      ? t('voices:actions.pause')
                      : t('voices:actions.preview')}
                  </Button>
                )}
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopyId(voice.voice_id)}
                >
                  {t('voices:actions.copyId')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.emptyState}>
          <p>{t('voices:empty')}</p>
        </div>
      )}
    </Flex>
  );
}
